import React, { useState, useRef, useEffect } from "react";
import { Doughnut, Bar, Line } from "react-chartjs-2";
import article from "./StopLossContent";
import WaldoAd from '../AdComponent';
import { 
  Chart, 
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
} from "chart.js";
import './PagesCss/StockProfitCalculator.css';

Chart.register(
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
);

const PositionSizeCalculator = () => {
  const content = article.find((item) => item.id === 4);
  const [activeTab, setActiveTab] = useState("calculator");
  const [isMobile, setIsMobile] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [tradingStyle, setTradingStyle] = useState("swing");
  const [notifications, setNotifications] = useState([]);
  const [calculationHistory, setCalculationHistory] = useState([]);

  const [inputs, setInputs] = useState({
    // Account Information
    accountSize: "25000",
    riskPerTrade: "1",
    maxPortfolioRisk: "5",
    
    // Trade Setup
    entryPrice: "50.00",
    stopLossPrice: "45.00",
    takeProfitPrice: "60.00",
    
    // Risk Management
    riskRewardRatio: "2",
    volatility: "15",
    positionSizingMethod: "fixed",
    
    // Advanced Settings
    commission: "5.00",
    slippage: "0.10",
    marginRequired: "0",
    leverage: "1",
    
    // Portfolio Management
    correlation: "0.3",
    maxPositions: "5",
    currentPositions: "2",
    
    // Market Conditions
    marketVolatility: "VIX High",
    trendDirection: "Bullish"
  });

  const [positionSize, setPositionSize] = useState(null);
  const [portfolioAnalysis, setPortfolioAnalysis] = useState(null);
  const chartRef = useRef();

  // Check mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-calculate when inputs change
  useEffect(() => {
    if (activeTab === "calculator" || activeTab === "portfolio") {
      calculatePositionSize();
    }
  }, [inputs, activeTab]);

  const addNotification = (message, type = "info") => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(notif => notif.id !== id));
    }, 5000);
  };

  const handleChange = (e) => {
    setInputs({ ...inputs, [e.target.name]: e.target.value });
  };

  const calculatePositionSize = () => {
    const {
      accountSize,
      riskPerTrade,
      entryPrice,
      stopLossPrice,
      takeProfitPrice,
      commission,
      slippage,
      marginRequired,
      leverage,
      maxPortfolioRisk,
      currentPositions,
      maxPositions,
      correlation
    } = inputs;

    if (!accountSize || !riskPerTrade || !entryPrice || !stopLossPrice) {
      return;
    }

    const account = parseFloat(accountSize);
    const riskPercent = parseFloat(riskPerTrade) / 100;
    const entry = parseFloat(entryPrice);
    const stopLoss = parseFloat(stopLossPrice);
    const takeProfit = parseFloat(takeProfitPrice) || 0;
    const comm = parseFloat(commission) || 0;
    const slip = parseFloat(slippage) || 0;
    const margin = parseFloat(marginRequired) || 0;
    const lev = parseFloat(leverage) || 1;
    const maxPortfolioRiskPercent = parseFloat(maxPortfolioRisk) / 100;
    const currentPos = parseInt(currentPositions) || 0;
    const maxPos = parseInt(maxPositions) || 5;
    const corr = parseFloat(correlation) || 0.3;

    // Calculate risk per share
    const riskPerShare = Math.abs(entry - stopLoss);
    const dollarRisk = account * riskPercent;

    // Calculate position size using different methods
    let shares = 0;
    let positionValue = 0;
    let actualRisk = 0;

    // Fixed fractional position sizing
    if (inputs.positionSizingMethod === "fixed") {
      shares = Math.floor((dollarRisk - comm * 2) / (riskPerShare + slip * 2));
      positionValue = shares * entry;
      actualRisk = shares * riskPerShare + comm * 2 + slip * 2;
    }
    // Kelly Criterion
    else if (inputs.positionSizingMethod === "kelly") {
      const winProb = 0.6; // Assume 60% win rate for Kelly
      const lossProb = 0.4;
      const winAmount = takeProfit ? Math.abs(takeProfit - entry) : riskPerShare * parseFloat(inputs.riskRewardRatio);
      const lossAmount = riskPerShare;
      const kellyFraction = winProb / lossAmount - lossProb / winAmount;
      const optimalFraction = Math.min(kellyFraction, riskPercent * 2); // Cap at 2x normal risk
      shares = Math.floor((account * optimalFraction) / riskPerShare);
      positionValue = shares * entry;
      actualRisk = shares * riskPerShare;
    }
    // Volatility adjusted
    else if (inputs.positionSizingMethod === "volatility") {
      const volatility = parseFloat(inputs.volatility) / 100;
      const volatilityAdjustment = Math.max(0.5, Math.min(2, 15 / volatility)); // Adjust based on volatility
      const adjustedRisk = dollarRisk * volatilityAdjustment;
      shares = Math.floor(adjustedRisk / riskPerShare);
      positionValue = shares * entry;
      actualRisk = shares * riskPerShare;
    }

    // Apply leverage
    if (lev > 1) {
      positionValue *= lev;
      actualRisk *= lev;
    }

    // Calculate risk metrics
    const riskPercentage = (actualRisk / account) * 100;
    const portfolioRisk = (actualRisk / account) * (1 + corr * currentPos);
    const maxAllowedRisk = account * maxPortfolioRiskPercent;
    const isWithinPortfolioLimit = actualRisk <= maxAllowedRisk;

    // Calculate profit potential
    const potentialProfit = takeProfit ? shares * (takeProfit - entry) - comm * 2 - slip * 2 : 0;
    const potentialProfitPercent = (potentialProfit / account) * 100;
    const riskReward = takeProfit ? Math.abs((takeProfit - entry) / (entry - stopLoss)) : parseFloat(inputs.riskRewardRatio) || 0;

    // Margin calculations
    const marginUsed = margin > 0 ? positionValue / margin : 0;
    const marginMaintenance = marginUsed * 0.25; // Assume 25% maintenance margin

    const result = {
      position: {
        shares: shares,
        positionValue: positionValue,
        riskPerShare: riskPerShare,
        dollarRisk: actualRisk,
        riskPercentage: riskPercentage,
        commission: comm * 2,
        slippage: slip * 2
      },
      risk: {
        riskRewardRatio: riskReward,
        potentialProfit: potentialProfit,
        potentialProfitPercent: potentialProfitPercent,
        stopLossPrice: stopLoss,
        takeProfitPrice: takeProfit,
        breakEvenPrice: entry + (comm * 2 + slip * 2) / shares
      },
      portfolio: {
        currentPositions: currentPos,
        maxPositions: maxPos,
        portfolioRisk: portfolioRisk * 100,
        maxPortfolioRisk: maxPortfolioRiskPercent * 100,
        isWithinLimit: isWithinPortfolioLimit,
        availableRisk: maxAllowedRisk - actualRisk,
        correlationImpact: corr * 100
      },
      margin: {
        leverage: lev,
        marginUsed: marginUsed,
        marginMaintenance: marginMaintenance,
        marginCallPrice: margin > 0 ? entry * (1 - (1 / margin) * 0.75) : 0 // 75% of margin used
      },
      metrics: {
        accountSize: account,
        riskPerTrade: riskPercent * 100,
        positionSizingMethod: inputs.positionSizingMethod
      }
    };

    setPositionSize(result);

    // Calculate portfolio analysis
    calculatePortfolioAnalysis(result, account, maxPos, currentPos);
  };

  const calculatePortfolioAnalysis = (position, accountSize, maxPositions, currentPositions) => {
    const availableSlots = maxPositions - currentPositions;
    const riskPerSlot = (parseFloat(inputs.maxPortfolioRisk) / 100) * accountSize / maxPositions;
    const currentRisk = position.position.dollarRisk;
    const riskUtilization = (currentRisk / riskPerSlot) * 100;

    const analysis = {
      availableSlots: availableSlots,
      riskPerSlot: riskPerSlot,
      currentRisk: currentRisk,
      riskUtilization: riskUtilization,
      recommendedMaxRisk: riskPerSlot * 0.8, // 80% of slot risk
      isOverAllocated: riskUtilization > 100,
      portfolioDiversity: (currentPositions / maxPositions) * 100
    };

    setPortfolioAnalysis(analysis);
  };

  const getRiskLevel = (riskPercentage) => {
    if (riskPercentage <= 0.5) return { level: "Very Low", color: "#28A745", description: "Conservative" };
    if (riskPercentage <= 1) return { level: "Low", color: "#2B8C3A", description: "Moderate" };
    if (riskPercentage <= 2) return { level: "Medium", color: "#FFC107", description: "Balanced" };
    if (riskPercentage <= 3) return { level: "High", color: "#FF6B6B", description: "Aggressive" };
    return { level: "Very High", color: "#E53E3E", description: "Speculative" };
  };

  const getPositionSizeRecommendation = (position) => {
    const riskLevel = getRiskLevel(position.riskPercentage);
    const recommendations = [];

    if (position.riskPercentage > parseFloat(inputs.riskPerTrade) * 1.2) {
      recommendations.push({
        type: "warning",
        message: "Position size exceeds recommended risk. Consider reducing shares.",
        action: "Reduce position by " + Math.round((position.position.shares * 0.2)) + " shares"
      });
    }

    if (!position.portfolio.isWithinLimit) {
      recommendations.push({
        type: "danger",
        message: "Exceeds portfolio risk limit. Position too large.",
        action: "Reduce position size or adjust portfolio limits"
      });
    }

    if (position.margin.marginUsed > 0.5) {
      recommendations.push({
        type: "warning",
        message: "High margin usage. Monitor closely.",
        action: "Consider reducing leverage"
      });
    }

    if (position.risk.riskRewardRatio < 1.5) {
      recommendations.push({
        type: "info",
        message: "Risk/reward ratio below optimal level.",
        action: "Look for better entry or adjust targets"
      });
    }

    if (recommendations.length === 0) {
      recommendations.push({
        type: "success",
        message: "Position size looks good. Meets all risk criteria.",
        action: "Proceed with trade"
      });
    }

    return recommendations;
  };

  const resetCalculator = () => {
    setInputs({
      accountSize: "25000",
      riskPerTrade: "1",
      maxPortfolioRisk: "5",
      entryPrice: "50.00",
      stopLossPrice: "45.00",
      takeProfitPrice: "60.00",
      riskRewardRatio: "2",
      volatility: "15",
      positionSizingMethod: "fixed",
      commission: "5.00",
      slippage: "0.10",
      marginRequired: "0",
      leverage: "1",
      correlation: "0.3",
      maxPositions: "5",
      currentPositions: "2",
      marketVolatility: "VIX High",
      trendDirection: "Bullish"
    });
    addNotification("Calculator reset to default values", "success");
  };

  const exportToCSV = () => {
    if (!positionSize) return;
    
    const csvContent = [
      "Metric,Value",
      `Account Size,$${positionSize.metrics.accountSize}`,
      `Risk Per Trade,${positionSize.metrics.riskPerTrade}%`,
      `Position Size,${positionSize.position.shares} shares`,
      `Position Value,$${positionSize.position.positionValue.toFixed(2)}`,
      `Dollar Risk,$${positionSize.position.dollarRisk.toFixed(2)}`,
      `Risk Percentage,${positionSize.position.riskPercentage.toFixed(2)}%`,
      `Risk/Reward Ratio,${positionSize.risk.riskRewardRatio.toFixed(2)}`,
      `Stop Loss,$${positionSize.risk.stopLossPrice}`,
      `Take Profit,$${positionSize.risk.takeProfitPrice || 'N/A'}`,
      `Portfolio Risk,${positionSize.portfolio.portfolioRisk.toFixed(2)}%`,
      `Leverage,${positionSize.margin.leverage}x`
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'position-size-analysis.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    addNotification("Analysis exported to CSV successfully", "success");
  };

  const printResult = () => {
    window.print();
  };

  const saveToHistory = () => {
    if (!positionSize) return;
    
    const newCalculation = {
      id: Date.now(),
      timestamp: new Date().toLocaleString(),
      inputs: { ...inputs },
      position: positionSize
    };
    
    setCalculationHistory(prev => [newCalculation, ...prev.slice(0, 9)]);
    addNotification("Calculation saved to history", "success");
  };

  const quickPreset = (preset) => {
    const presets = {
      conservative: { riskPerTrade: "0.5", positionSizingMethod: "fixed", leverage: "1" },
      moderate: { riskPerTrade: "1", positionSizingMethod: "fixed", leverage: "1" },
      aggressive: { riskPerTrade: "2", positionSizingMethod: "volatility", leverage: "2" }
    };
    
    setInputs(prev => ({ ...prev, ...presets[preset] }));
    addNotification(`${preset} trading preset applied`, "info");
  };

  const tradingStylePreset = (style) => {
    const presets = {
      day: { riskPerTrade: "0.5", maxPortfolioRisk: "10", positionSizingMethod: "volatility" },
      swing: { riskPerTrade: "1", maxPortfolioRisk: "5", positionSizingMethod: "fixed" },
      position: { riskPerTrade: "2", maxPortfolioRisk: "3", positionSizingMethod: "kelly" }
    };
    
    setInputs(prev => ({ ...prev, ...presets[style] }));
    setTradingStyle(style);
    addNotification(`${style} trading style applied`, "info");
  };

  // Chart Data
  const riskChartData = positionSize ? {
    labels: ["Position Risk", "Available Risk"],
    datasets: [{
      data: [
        positionSize.position.dollarRisk,
        Math.max(positionSize.metrics.accountSize * (parseFloat(inputs.maxPortfolioRisk) / 100) - positionSize.position.dollarRisk, 0)
      ],
      backgroundColor: [
        positionSize.portfolio.isWithinLimit ? "#FFC107" : "#E53E3E",
        "#28A745"
      ],
      borderWidth: 2,
      hoverOffset: 15
    }],
  } : null;

  const positionChartData = positionSize ? {
    labels: ['Position Value', 'Risk Amount', 'Potential Profit'],
    datasets: [{
      data: [
        positionSize.position.positionValue,
        positionSize.position.dollarRisk,
        Math.max(positionSize.risk.potentialProfit, 0)
      ],
      backgroundColor: ["#19692C", "#FF6B6B", "#28A745"],
      borderWidth: 2
    }]
  } : null;

  const portfolioChartData = portfolioAnalysis ? {
    labels: ['Used Risk', 'Available Risk', 'Unused Capacity'],
    datasets: [{
      data: [
        portfolioAnalysis.currentRisk,
        portfolioAnalysis.availableSlots * portfolioAnalysis.riskPerSlot,
        portfolioAnalysis.recommendedMaxRisk * portfolioAnalysis.availableSlots
      ],
      backgroundColor: ["#FF6B6B", "#FFC107", "#28A745"],
      borderWidth: 2
    }]
  } : null;

  const riskLevel = positionSize ? getRiskLevel(positionSize.position.riskPercentage) : null;
  const recommendations = positionSize ? getPositionSizeRecommendation(positionSize) : [];

  return (
    <div className={'profit-calculator'}>
      {/* Notifications */}
      <div className="notifications-container">
        {notifications.map(notif => (
          <div key={notif.id} className={`notification ${notif.type}`}>
            <i className={`fas fa-${notif.type === 'success' ? 'check' : 'info'}-circle`}></i>
            {notif.message}
          </div>
        ))}
      </div>

      <div className="calculator-header">
        <div className="header-top">
          <h1>Position Size Calculator</h1>
        </div>
        <p>Optimize your trade size and manage risk with professional position sizing</p>
      </div>
<WaldoAd type="horizontal" /><br/>
      <div className="calculator-layout">
        {/* Left Side - Inputs */}
        <div className="left-panel">
          {/* Navigation Tabs */}
          <div className="tabs-container">
            <div className="tabs">
              <button 
                className={`tab ${activeTab === "calculator" ? "active" : ""}`}
                onClick={() => setActiveTab("calculator")}
              >
                <i className="fas fa-calculator"></i>
                {isMobile ? 'Calculator' : 'Position Sizing'}
              </button>
              <button 
                className={`tab ${activeTab === "portfolio" ? "active" : ""}`}
                onClick={() => setActiveTab("portfolio")}
              >
                <i className="fas fa-chart-pie"></i>
                {isMobile ? 'Portfolio' : 'Portfolio Risk'}
              </button>
              <button 
                className={`tab ${activeTab === "analysis" ? "active" : ""}`}
                onClick={() => setActiveTab("analysis")}
              >
                <i className="fas fa-chart-bar"></i>
                {isMobile ? 'Analysis' : 'Risk Analysis'}
              </button>
              <button 
                className={`tab ${activeTab === "history" ? "active" : ""}`}
                onClick={() => setActiveTab("history")}
              >
                <i className="fas fa-history"></i>
                {isMobile ? 'History' : 'History'}
              </button>
            </div>
          </div>

          {/* Risk Profile Presets */}
          <div className="presets-section">
            <h4>Risk Profile:</h4>
            <div className="preset-buttons">
              <button className="preset-btn conservative" onClick={() => quickPreset('conservative')}>
                Conservative
              </button>
              <button className="preset-btn moderate" onClick={() => quickPreset('moderate')}>
                Moderate
              </button>
              <button className="preset-btn aggressive" onClick={() => quickPreset('aggressive')}>
                Aggressive
              </button>
            </div>
          </div>

          {/* Trading Style Selection */}
          <div className="mode-selection">
            <div className="mode-buttons">
              <button 
                className={`mode-btn ${tradingStyle === "day" ? "active" : ""}`}
                onClick={() => tradingStylePreset('day')}
              >
                <i className="fas fa-bolt"></i>
                Day Trading
              </button>
              <button 
                className={`mode-btn ${tradingStyle === "swing" ? "active" : ""}`}
                onClick={() => tradingStylePreset('swing')}
              >
                <i className="fas fa-wave-square"></i>
                Swing Trading
              </button>
              <button 
                className={`mode-btn ${tradingStyle === "position" ? "active" : ""}`}
                onClick={() => tradingStylePreset('position')}
              >
                <i className="fas fa-mountain"></i>
                Position Trading
              </button>
            </div>
            <div className="mode-description">
              {tradingStyle === "day" 
                ? "Short-term trades with smaller position sizes and higher frequency"
                : tradingStyle === "swing"
                ? "Medium-term trades with balanced risk and moderate holding periods"
                : "Long-term investments with larger positions and lower turnover"
              }
            </div>
          </div>

          {/* Position Sizing Tab */}
          {activeTab === "calculator" && (
            <div className="calculator-content">
              <div className="input-section">
                <div className="input-group">
                  <h3>
                    <i className="fas fa-wallet"></i>
                    Account Information
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-dollar-sign"></i> Account Size</label>
                      <input 
                        type="number" 
                        name="accountSize" 
                        value={inputs.accountSize} 
                        onChange={handleChange}
                        placeholder="25000"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-exclamation-triangle"></i> Risk Per Trade (%)</label>
                      <input 
                        type="number" 
                        name="riskPerTrade" 
                        value={inputs.riskPerTrade} 
                        onChange={handleChange}
                        placeholder="1"
                        step="0.1"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-shield-alt"></i> Max Portfolio Risk (%)</label>
                      <input 
                        type="number" 
                        name="maxPortfolioRisk" 
                        value={inputs.maxPortfolioRisk} 
                        onChange={handleChange}
                        placeholder="5"
                        step="0.1"
                      />
                    </div>
                  </div>

                  <h3>
                    <i className="fas fa-chart-line"></i>
                    Trade Setup
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-arrow-right"></i> Entry Price</label>
                      <input 
                        type="number" 
                        name="entryPrice" 
                        value={inputs.entryPrice} 
                        onChange={handleChange}
                        placeholder="50.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-arrow-down"></i> Stop Loss Price</label>
                      <input 
                        type="number" 
                        name="stopLossPrice" 
                        value={inputs.stopLossPrice} 
                        onChange={handleChange}
                        placeholder="45.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-arrow-up"></i> Take Profit Price</label>
                      <input 
                        type="number" 
                        name="takeProfitPrice" 
                        value={inputs.takeProfitPrice} 
                        onChange={handleChange}
                        placeholder="60.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-balance-scale"></i> Risk/Reward Ratio</label>
                      <input 
                        type="number" 
                        name="riskRewardRatio" 
                        value={inputs.riskRewardRatio} 
                        onChange={handleChange}
                        placeholder="2"
                        step="0.1"
                      />
                    </div>
                  </div>

                  <h3>
                    <i className="fas fa-sliders-h"></i>
                    Risk Management
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-wave-square"></i> Volatility (%)</label>
                      <input 
                        type="number" 
                        name="volatility" 
                        value={inputs.volatility} 
                        onChange={handleChange}
                        placeholder="15"
                        step="0.1"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-calculator"></i> Position Sizing Method</label>
                      <select name="positionSizingMethod" value={inputs.positionSizingMethod} onChange={handleChange}>
                        <option value="fixed">Fixed Fractional</option>
                        <option value="kelly">Kelly Criterion</option>
                        <option value="volatility">Volatility Adjusted</option>
                      </select>
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-rocket"></i> Leverage</label>
                      <select name="leverage" value={inputs.leverage} onChange={handleChange}>
                        <option value="1">1x (No Leverage)</option>
                        <option value="2">2x</option>
                        <option value="3">3x</option>
                        <option value="5">5x</option>
                        <option value="10">10x</option>
                      </select>
                    </div>
                  </div>

                  <h3>
                    <i className="fas fa-cog"></i>
                    Advanced Settings
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-coins"></i> Commission per Trade</label>
                      <input 
                        type="number" 
                        name="commission" 
                        value={inputs.commission} 
                        onChange={handleChange}
                        placeholder="5.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-arrows-left-right"></i> Slippage per Share</label>
                      <input 
                        type="number" 
                        name="slippage" 
                        value={inputs.slippage} 
                        onChange={handleChange}
                        placeholder="0.10"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-percentage"></i> Margin Required</label>
                      <input 
                        type="number" 
                        name="marginRequired" 
                        value={inputs.marginRequired} 
                        onChange={handleChange}
                        placeholder="0"
                        step="0.01"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="action-buttons">
                <button className="btn reset-btn" onClick={resetCalculator}>
                  <i className="fas fa-redo"></i>
                  Reset
                </button>
                <button className="btn save-btn" onClick={saveToHistory}>
                  <i className="fas fa-save"></i>
                  Save Calculation
                </button>
              </div>
            </div>
          )}
<WaldoAd type="square" />
          {/* Portfolio Risk Tab */}
          {activeTab === "portfolio" && (
            <div className="projection-content">
              <div className="input-section">
                <h3>
                  <i className="fas fa-chart-pie"></i>
                  Portfolio Management
                </h3>
                <div className="input-row">
                  <div className="input-field">
                    <label><i className="fa-solid fa-layer-group"></i> Max Open Positions</label>
                    <input 
                      type="number" 
                      name="maxPositions" 
                      value={inputs.maxPositions} 
                      onChange={handleChange}
                      placeholder="5"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-chart-line"></i> Current Open Positions</label>
                    <input 
                      type="number" 
                      name="currentPositions" 
                      value={inputs.currentPositions} 
                      onChange={handleChange}
                      placeholder="2"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-link"></i> Position Correlation</label>
                    <select name="correlation" value={inputs.correlation} onChange={handleChange}>
                      <option value="0.1">Low (0.1)</option>
                      <option value="0.3">Medium (0.3)</option>
                      <option value="0.5">High (0.5)</option>
                      <option value="0.7">Very High (0.7)</option>
                    </select>
                  </div>
                </div><br/>

                <h3>
                  <i className="fas fa-chart-bar"></i>
                  Market Conditions
                </h3>
                <div className="input-row">
                  <div className="input-field">
                    <label><i className="fa-solid fa-chart-line"></i> Market Volatility</label>
                    <select name="marketVolatility" value={inputs.marketVolatility} onChange={handleChange}>
                      <option value="VIX Low">Low Volatility</option>
                      <option value="VIX Medium">Medium Volatility</option>
                      <option value="VIX High">High Volatility</option>
                      <option value="VIX Extreme">Extreme Volatility</option>
                    </select>
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-arrow-trend-up"></i> Trend Direction</label>
                    <select name="trendDirection" value={inputs.trendDirection} onChange={handleChange}>
                      <option value="Bullish">Bullish</option>
                      <option value="Neutral">Neutral</option>
                      <option value="Bearish">Bearish</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* History Tab */}
          {activeTab === "history" && (
            <div className="history-content">
              <h3>
                <i className="fas fa-history"></i>
                Position History
              </h3>
              {calculationHistory.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-inbox fa-3x"></i>
                  <p>No calculations yet. Start sizing positions to see your history here.</p>
                </div>
              ) : (
                <div className="history-list">
                  {calculationHistory.map(calc => (
                    <div key={calc.id} className="history-item">
                      <div className="history-header">
                        <span className="history-date">
                          <i className="fas fa-clock"></i>
                          {calc.timestamp}
                        </span>
                        <span className="history-profit positive">
                          <i className="fas fa-chart-line"></i>
                          {calc.position.position.shares} shares
                        </span> 
                      </div>
                      <div className="history-details">
                        <span><i className="fas fa-dollar-sign"></i> Size: ${calc.position.position.positionValue.toFixed(0)}</span>
                        <span><i className="fas fa-exclamation-triangle"></i> Risk: {calc.position.position.riskPercentage.toFixed(1)}%</span>
                        <span><i className="fas fa-balance-scale"></i> R:R: {calc.position.risk.riskRewardRatio.toFixed(1)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Side - Results */}
        {(activeTab === "calculator" || activeTab === "portfolio" || activeTab === "analysis") && (
          <div className="right-panel">
            {activeTab === "calculator" && (
              <div className="results-section">
                <div className="results-header">
                  <h2>
                    <i className="fas fa-calculator"></i>
                    Position Size Results
                  </h2>
                  <div className="result-actions">
                    <button className="btn export-btn" onClick={exportToCSV}>
                      <i className="fas fa-file-export"></i>
                      {isMobile ? 'CSV' : 'Export CSV'}
                    </button>
                    <button className="btn print-btn" onClick={printResult}>
                      <i className="fas fa-print"></i>
                      {isMobile ? 'Print' : 'Print'}
                    </button>
                  </div>
                </div>
<WaldoAd type="square" />
                {positionSize ? (
                  <>
                    {/* Position Size Results */}
                    <div className="results-grid">
                      <div className="result-card highlight">
                        <div className="result-value" style={{ color: '#FFFFFF' }}>
                          {positionSize.position.shares}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-layer-group"></i>
                          Shares to Buy
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          ${positionSize.position.positionValue.toLocaleString(undefined, {maximumFractionDigits: 0})}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-dollar-sign"></i>
                          Position Value
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value" style={{ color: riskLevel?.color }}>
                          ${positionSize.position.dollarRisk.toFixed(0)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-exclamation-triangle"></i>
                          Dollar Risk
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value" style={{ color: riskLevel?.color }}>
                          {positionSize.position.riskPercentage.toFixed(2)}%
                        </div>
                        <div className="result-label">
                          <i className="fas fa-percentage"></i>
                          Risk Percentage
                        </div>
                      </div>
                    </div>

                    {/* Risk Level */}
                    <div className="risk-summary">
                      <h4>Risk Level: <span style={{ color: riskLevel?.color }}>{riskLevel?.level} - {riskLevel?.description}</span></h4>
                      <div className="risk-meter">
                        <div className="meter-bar">
                          <div 
                            className="meter-fill"
                            style={{ 
                              width: `${Math.min(positionSize.position.riskPercentage * 20, 100)}%`,
                              backgroundColor: riskLevel?.color
                            }}
                          ></div>
                        </div>
                        <div className="meter-value" style={{ color: riskLevel?.color }}>
                          {positionSize.position.riskPercentage.toFixed(2)}% of account at risk
                        </div>
                      </div>
                    </div>

                    {/* Trade Details */}
                    <div className="detailed-results">
                      <div className="detail-row">
                        <span><i className="fas fa-arrow-down"></i> Stop Loss Price:</span>
                        <span>${positionSize.risk.stopLossPrice}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-arrow-up"></i> Take Profit Price:</span>
                        <span>${positionSize.risk.takeProfitPrice || 'Not set'}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-balance-scale"></i> Risk/Reward Ratio:</span>
                        <span>{positionSize.risk.riskRewardRatio.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-calculator"></i> Break-even Price:</span>
                        <span>${positionSize.risk.breakEvenPrice.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-coins"></i> Total Commission & Slippage:</span>
                        <span>${(positionSize.position.commission + positionSize.position.slippage).toFixed(2)}</span>
                      </div>
                    </div>

                    {/* Recommendations */}
                    <div className="risk-summary">
                      <h4>Trade Recommendations</h4>
                      <div className="scenario-list">
                        {recommendations.map((rec, index) => (
                          <div key={index} className="scenario-item">
                            <div className="scenario-name">
                              <i className={`fas fa-${rec.type === 'success' ? 'check' : rec.type === 'warning' ? 'exclamation-triangle' : 'exclamation-circle'} fa-xs`}></i>
                              {rec.message}
                            </div>
                            <div className="scenario-outcome">{rec.action}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Risk Distribution */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-chart-pie"></i>
                        Risk Allocation
                      </h3>
                      <Doughnut 
                        data={riskChartData} 
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              position: isMobile ? 'top' : 'bottom'
                            },
                            tooltip: {
                              callbacks: {
                                label: function(context) {
                                  return `$${context.parsed.toFixed(0)}`;
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>

                    {/* Position Composition */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-chart-bar"></i>
                        Position Composition
                      </h3>
                      <Bar 
                        data={positionChartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              display: false
                            }
                          },
                          scales: {
                            y: {
                              beginAtZero: true,
                              ticks: {
                                callback: function(value) {
                                  return '$' + value.toLocaleString(undefined, {maximumFractionDigits: 0});
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>
                  </>
                ) : (
                  <div className="empty-results">
                    <i className="fas fa-calculator fa-3x"></i>
                    <p>Enter your trade details to calculate position size</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === "portfolio" && portfolioAnalysis && (
              <div className="projection-results">
                <h3>
                  <i className="fas fa-chart-pie"></i>
                  Portfolio Risk Analysis
                </h3>

                {/* Portfolio Summary */}
                <div className="projection-summary">
                  <div className="summary-card">
                    <div className="summary-value" style={{ color: portfolioAnalysis.isOverAllocated ? '#E53E3E' : '#28A745' }}>
                      {portfolioAnalysis.riskUtilization.toFixed(0)}%
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-chart-pie"></i>
                      Risk Slot Utilization
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-value">
                      {portfolioAnalysis.availableSlots}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-layer-group"></i>
                      Available Slots
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-value">
                      {portfolioAnalysis.portfolioDiversity.toFixed(0)}%
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-sitemap"></i>
                      Portfolio Diversity
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-value">
                      ${portfolioAnalysis.recommendedMaxRisk.toFixed(0)}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-bullseye"></i>
                      Recommended Max Risk
                    </div>
                  </div>
                </div><br/>

                {/* Portfolio Allocation */}
                <div className="chart-container">
                  <h3>
                    <i className="fas fa-chart-pie"></i>
                    Portfolio Risk Allocation
                  </h3>
                  <Doughnut 
                    data={portfolioChartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: true,
                      plugins: {
                        legend: {
                          position: isMobile ? 'top' : 'right'
                        },
                        tooltip: {
                          callbacks: {
                            label: function(context) {
                              return `$${context.parsed.toLocaleString(undefined, {maximumFractionDigits: 0})}`;
                            }
                          }
                        }
                      }
                    }}
                  />
                </div>

                {/* Portfolio Recommendations */}
                <div className="risk-summary">
                  <h4>Portfolio Recommendations</h4>
                  <div className="scenario-list">
                    {portfolioAnalysis.isOverAllocated ? (
                      <div className="scenario-item">
                        <div className="scenario-name">
                          <i className="fas fa-exclamation-triangle fa-xs"></i>
                          Risk Overallocated
                        </div>
                        <div className="scenario-outcome">Consider reducing position size or closing other positions</div>
                      </div>
                    ) : (
                      <div className="scenario-item">
                        <div className="scenario-name">
                          <i className="fas fa-check fa-xs"></i>
                          Risk Within Limits
                        </div>
                        <div className="scenario-outcome">Portfolio risk allocation looks good</div>
                      </div>
                    )}
                    {portfolioAnalysis.availableSlots === 0 && (
                      <div className="scenario-item">
                        <div className="scenario-name">
                          <i className="fas fa-exclamation-circle fa-xs"></i>
                          No Available Slots
                        </div>
                        <div className="scenario-outcome">Close existing positions before opening new ones</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === "analysis" && positionSize && (
              <div className="risk-results-panel">
                <h3>
                  <i className="fas fa-chart-bar"></i>
                  Advanced Risk Analysis
                </h3><br/>
                
                {/* Margin Analysis */}
                <div className="scenario-comparison">
                  <div className="scenario-item">
                    <div className="scenario-name">Margin Analysis</div>
                    <div className="scenario-stats">
                      <div className="breakdown-item">
                        <span>Leverage Used:</span>
                        <span>{positionSize.margin.leverage}x</span>
                      </div>
                      <div className="breakdown-item">
                        <span>Margin Utilization:</span>
                        <span>{(positionSize.margin.marginUsed * 100).toFixed(1)}%</span>
                      </div>
                      <div className="breakdown-item">
                        <span>Margin Call Price:</span>
                        <span>${positionSize.margin.marginCallPrice.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="scenario-item">
                    <div className="scenario-name">Portfolio Impact</div>
                    <div className="scenario-stats">
                      <div className="breakdown-item">
                        <span>Adjusted Portfolio Risk:</span>
                        <span>{positionSize.portfolio.portfolioRisk.toFixed(2)}%</span>
                      </div>
                      <div className="breakdown-item">
                        <span>Correlation Impact:</span>
                        <span>+{positionSize.portfolio.correlationImpact.toFixed(1)}%</span>
                      </div>
                      <div className="breakdown-item">
                        <span>Within Portfolio Limits:</span>
                        <span style={{ color: positionSize.portfolio.isWithinLimit ? '#28A745' : '#E53E3E' }}>
                          {positionSize.portfolio.isWithinLimit ? 'Yes' : 'No'}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="scenario-item">
                    <div className="scenario-name">Profit Potential</div>
                    <div className="scenario-stats">
                      <div className="breakdown-item">
                        <span>Potential Profit:</span>
                        <span>${positionSize.risk.potentialProfit.toFixed(0)}</span>
                      </div>
                      <div className="breakdown-item">
                        <span>Return on Account:</span>
                        <span>{positionSize.risk.potentialProfitPercent.toFixed(2)}%</span>
                      </div>
                      <div className="breakdown-item">
                        <span>Risk/Reward Quality:</span>
                        <span style={{ 
                          color: positionSize.risk.riskRewardRatio >= 2 ? '#28A745' : 
                                 positionSize.risk.riskRewardRatio >= 1.5 ? '#FFC107' : '#E53E3E'
                        }}>
                          {positionSize.risk.riskRewardRatio >= 2 ? 'Excellent' : 
                           positionSize.risk.riskRewardRatio >= 1.5 ? 'Good' : 'Poor'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <br/><WaldoAd type="horizontal" />
      {content && content.content}
    </div>
  );
};

export default PositionSizeCalculator;